import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, Transition as _Transition, createBlock as _createBlock, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "max-h-[100vh] h-screen flex flex-col" }
const _hoisted_2 = { class: "flex items-center gap-2 leading-none" }
const _hoisted_3 = {
  key: 0,
  class: "flex items-center flex-row gap-2"
}
const _hoisted_4 = { class: "py-1 divide-y" }
const _hoisted_5 = {
  key: 1,
  class: "absolute inset-0 flex items-center justify-center",
  style: {"z-index":"9999"}
}

import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { EllipsisVerticalIcon } from "@heroicons/vue/24/outline";
import { isWithinInterval, parse } from "date-fns";
import { nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import * as XLSX from "xlsx";
import { segmentAnalytics } from "@/assets/scripts";
import NavigationComponent from "@/components/layout/NavigationComponent.vue";
import NoDataYet from "@/components/layout/NoDataYet.vue";
import PageHeader from "@/components/layout/PageHeader.vue";
import LoadingSection from "@/components/loading_state/LoadingSection.vue";
import TableResizeSlider from "@/components/other/TableResizeSlider.vue";
import { useHasProjectFeature } from "@/composables/project";
import ProcessRepository from "@/repositories/OpsProcessesRepository";
import textService from "@/services/textService";
import { HierarchyType } from "@/types/HierarchyTag";
import {
  FilteredResource,
  ResourceGroup,
  ShortenedProcess,
  TagGroupedProcess,
} from "@/types/Process";
import { TableResizeSliderEvent } from "@/types/Tables";
import ProcessSideBarContent from "@/views/planner/components/ProcessSideBarContent.vue";
import { ProcessResourceModel } from "@/views/process_gantt/SchedulerProConfig";
import Table from "@/views/process_table/components/Table.vue";
import { usePlainPresets } from "@/views/process_table/components/plainView";
import { Column, Data } from "@/views/process_table/types";

const sliderWidth = 20;
type SliderName = "full";

export default /*#__PURE__*/_defineComponent({
  __name: 'ProcessTable',
  setup(__props) {

const { t } = useI18n();
const { params, query } = useRoute();
const processTable = ref<typeof Table>();
const data = ref<Data[]>([]);
const filteredDataLength = ref(0);
const loading = ref(true);
const selectedProcess = ref<Data | null>(null);
const selectedProcesses = ref<ShortenedProcess[]>([]);
const selectedResourceName = ref("");
const columns = ref<Column[]>([]);
const summary = ref<Record<string, string>>();
const sortedBy = ref<"end" | "date">("date");
const noData = ref(false);
const workingHoursFeatureEnabled = useHasProjectFeature("working_hours");
const levelOrder = ref<Record<string, number>>({});
const { columns: pColumns, dataMapper: pDataMapper, getSummary: pGetSummary } = usePlainPresets();
const processes = ref<TagGroupedProcess[]>([]);
const filterResources = ref({
  building: {} as Record<string, boolean>,
  level: {} as Record<string, boolean>,
  component: {} as Record<string, boolean>,
  section: {} as Record<string, boolean>,
} as FilteredResource);
const route = useRoute();

const draggingSliderName = ref<SliderName | null>(null);
const processSideBarRef = ref<HTMLDivElement | null>(null);
const getContainerRef = () => processTableRef;

const windowWidth = ref<number>(window.innerWidth);

const handleResize = () => {
  if (processTableRef.value) {
    contentWidth.value = processTableRef.value.getBoundingClientRect().width;
  }
};

const getQueryStringsData = () => {
  Object.keys(route.query).forEach((key) => {
    if (key === "processes" || key === "processTypes") {
      columns.value.forEach((item) => {
        if (item.field === "process") {
          item.filters?.forEach((filter) => {
            if (
              filter.filterValue &&
              typeof filter.filterValue === "object" &&
              !Array.isArray(filter.filterValue)
            ) {
              filter.filterValue[key] = route.query[key]
                ? (route.query[key] as string).split(",")
                : [];
            }
          });
        }
      });
    }
    if (["building", "level", "section", "component"].includes(key)) {
      columns.value.forEach((item) => {
        if (item.field === "location") {
          item.filters?.forEach((filter) => {
            if (
              filter.filterValue &&
              typeof filter.filterValue === "object" &&
              !Array.isArray(filter.filterValue)
            ) {
              filter.filterValue[key] = route.query[key]
                ? (route.query[key] as string).split(",")
                : [];
            }
          });
        }
      });
    }
    if (key === "date") {
      columns.value.forEach((item) => {
        if (item.field === "date") {
          item.filters?.forEach((filter) => {
            if (filter.filterValue && Array.isArray(filter.filterValue)) {
              filter.filterValue = route.query[key]
                ? (route.query[key] as string)
                    .split(",")
                    .map((dateStr) => parse(dateStr, "yyyy-MM-dd", new Date()))
                : [];
            }
          });
        }
      });
    }
  });
};

onMounted(async () => {
  loading.value = true;
  columns.value = pColumns;

  getQueryStringsData();
  windowWidth.value = window.innerWidth;

  processes.value = await loadData();
  const mappedData = pDataMapper(processes.value);
  summary.value = pGetSummary(mappedData);
  loading.value = false;

  nextTick(() => {
    data.value = mappedData;
    updateSelectedProcess(data.value);

    if (processTableRef.value) {
      contentWidth.value = processTableRef.value.getBoundingClientRect().width;
    }

    window.addEventListener("resize", handleResize);
  });

  segmentAnalytics("process_table_view_plain");
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});

const getTagName = (tags: Partial<TagGroupedProcess["tags"]>) => {
  return [tags.building, tags.level, tags.section, tags.component].filter(Boolean).join(" | ");
};

const getProcessesComponents = (processes: TagGroupedProcess[]) => {
  const components = new Set<Record<string, string>>();
  processes?.forEach((process: TagGroupedProcess) => {
    components.add(process?.tags);
  });
  return Array.from(components);
};

const resourceElements = ref<ResourceGroup[]>([]);
const resourceFilterGroups = (value: ProcessResourceModel[]): ResourceGroup[] => {
  const updatedResourceElements = [] as ResourceGroup[];

  const keys = Object.keys(value[0]?.component || {}) as HierarchyType[];

  value.forEach((item) => {
    keys.forEach((key) => {
      const existingGroup = updatedResourceElements.find((group) => group.field === key);
      if (!existingGroup) {
        const componentValue = (item.component as Record<string, string>)?.[key];
        if (componentValue !== undefined) {
          updatedResourceElements.push({
            name: t(`analytics.processes.${key}`),
            field: key,
            options: [{ name: componentValue, value: componentValue }],
          });
        }
      } else {
        const currentGroup = updatedResourceElements.find((group) => group.field === key);

        if (currentGroup) {
          const optionValue = (item.component as Record<string, string> | undefined)?.[key];

          if (optionValue !== undefined) {
            const newOption = { name: optionValue, value: optionValue };

            if (!currentGroup.options.some((opt) => opt.value === newOption.value)) {
              currentGroup.options.push(newOption);
            }

            if (currentGroup.field === "level") {
              currentGroup.options.sort((a, b) => {
                return (levelOrder.value[a.name] || 0) - (levelOrder.value[b.name] || 0);
              });
            } else {
              currentGroup.options.sort((a, b) => a.name.localeCompare(b.name));
            }
          }
        }
      }
    });
  });

  // Create a copy of filterResources.value
  const updatedFilterResources = { ...filterResources.value };

  updatedResourceElements.forEach((group) => {
    const field = group.field as keyof typeof filterResources.value;
    const newOptions = group.options.map((option) => option.value);
    updatedFilterResources[field] = Object.fromEntries(newOptions.map((value) => [value, true]));
  });
  // Return the updated value instead of modifying resourceElements
  return updatedResourceElements;
};

watch(processes, async () => {
  let lastDate = new Date(0);
  let startDate = new Date();

  if (processes.value?.length) {
    const resources: (ProcessResourceModel & Record<string, unknown>)[] = [];
    processes.value.forEach((item, i) => {
      item.tags = Object.fromEntries(
        Object.entries({
          building: item.tags.building,
          level: item.tags.level,
          section: item.tags.section,
          component: item.tags.component,
        }).filter(([, value]) => value),
      ) as typeof item.tags;

      resources.push({
        id: item._id,
        component: item.tags,
        index: i,
        name: getTagName(item.tags),
      });

      item.processes.forEach((process) => {
        if (process.end_time > lastDate) {
          lastDate = process.end_time;
        }
        if (process.start_time < startDate) {
          startDate = process.start_time;
        }
      });
    });

    const tags = getProcessesComponents(processes.value);

    filterResources.value = {
      building: Object.fromEntries(tags.map((value) => [value?.building, false])),
      level: Object.fromEntries(tags.map((value) => [value?.level, false])),
      section: Object.fromEntries(tags.map((value) => [value?.section, false])),
      component: Object.fromEntries(tags.map((value) => [value?.component, false])),
    };

    resourceElements.value = resourceFilterGroups(resources);
  }
});

const collectContextMenuStatistics = () => {
  segmentAnalytics("process_table_context_menu_opened");
};

const loadData = async () => {
  const data = await ProcessRepository.loadTagGroupedProcesses(
    params.customer_name as string,
    params.site_id as string,
  );
  if (data.data.length === 0) noData.value = true;
  levelOrder.value = data.levelOrders;
  return data.data;
};

const handleFilterDate = (value: [Date, Date], data: Data[]) => {
  const [start, end] = value;
  return data.filter((item) => isWithinInterval(item.date as Date, { start, end }));
};

const handleDataUpdate = (updatedData: Data[]) => {
  summary.value = pGetSummary(updatedData);
  updateSelectedProcess(updatedData);
  filteredDataLength.value = updatedData.length;
};

const updateSelectedProcess = (data: Data[]) => {
  if (data.length) {
    setTimeout(() => {
      const initialProcess = data.find((item) => item?._id === query.processId) || data[0];
      if (initialProcess) {
        initialProcess.class = "bg-yellow-200";
        selectedProcess.value = initialProcess;
        selectedProcesses.value = [initialProcess as ShortenedProcess];

        document
          .getElementById(selectedProcess.value._id as string)
          ?.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }, 100);
  }
};

const handleRowClick = (row: Data, dbClick = false) => {
  if (dbClick) {
    return;
  }

  selectedProcesses.value = [row as ShortenedProcess];
  selectedResourceName.value = [row.building, row.section, row.level, row.component]
    .filter(Boolean)
    .join(" > ");

  if (selectedProcess.value) {
    selectedProcess.value.class = "";
  }

  row.class = "bg-yellow-200";
  selectedProcess.value = row;
};

const handleExportToExcel = () => {
  const header = pColumns
    .filter((col) => col.field !== "working_hours" || workingHoursFeatureEnabled)
    .flatMap((col) => {
      if (col.field === "location") {
        return Object.keys(data.value[0]?.[col.field] || {}).map((tag) =>
          t(`analytics.processes.${tag}`),
        );
      }
      return col.label;
    });
  const mappedData: unknown[][] = [];

  mappedData.push(header);

  data.value.forEach((item) => {
    const row: unknown[] = [];
    pColumns.forEach((col) => {
      if (col.field === "location") {
        Object.values((item?.location as Record<string, string>) || {}).forEach((location) =>
          row.push(location),
        );
        return;
      }
      if (col.field !== "working_hours" || workingHoursFeatureEnabled) {
        row.push(col.render ? col.render(item[col.field]) : item[col.field]);
      }
    });
    mappedData.push(row);
  });
  const workbook = XLSX.utils.book_new();
  const worksheetProcessExport = XLSX.utils.aoa_to_sheet(mappedData);
  worksheetProcessExport["!cols"] = mappedData[0].map(() => ({ width: 30 }));
  XLSX.utils.book_append_sheet(workbook, worksheetProcessExport, t("app_features.processes"));
  XLSX.writeFile(
    workbook,
    `oculai_${textService.toNormalizedFileName(t("app_features.processes"))}.xlsx`,
  );
};

const sideBarWidth = ref(450);

const handleFullSliderMoved = (event: TableResizeSliderEvent) => {
  contentWidth.value = event.containerRect.width;

  const newSidebarWidth = event.containerRect.width - event.x;
  if (newSidebarWidth > 450 && newSidebarWidth < event.containerRect.width / 2) {
    sideBarWidth.value = newSidebarWidth;
  }
};

const processTableRef = ref();

const contentWidth = ref(0);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(NavigationComponent, {
    activeTag: _ctx.$t('app_features.processes')
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(PageHeader, null, {
          content: _withCtx(() => [
            handleExportToExcel
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createVNode(_unref(Menu), {
                    as: "div",
                    class: "text-left leading-none font-normal"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(MenuButton), { class: "flex items-center rounded-full text-gray-500 hover:text-gray-600" }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(EllipsisVerticalIcon), {
                            class: "h-8 w-8",
                            "aria-hidden": "true"
                          })
                        ]),
                        _: 1
                      }),
                      _createVNode(_Transition, {
                        "enter-active-class": "transition ease-out duration-100",
                        "enter-from-class": "transform opacity-0 scale-95",
                        "enter-to-class": "transform opacity-100 scale-100",
                        "leave-active-class": "transition ease-in duration-75",
                        "leave-from-class": "transform opacity-100 scale-100",
                        "leave-to-class": "transform opacity-0 scale-95"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(MenuItems), { class: "absolute right-5 z-20 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-gray ring-opacity-5 focus:outline-none" }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_4, [
                                _createVNode(_unref(MenuItem), { as: "template" }, {
                                  default: _withCtx(({ active }) => [
                                    _createVNode(_unref(Menu), {
                                      as: "div",
                                      class: _normalizeClass([
                          active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                          'extraMenuItem',
                        ])
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_unref(MenuButton), {
                                          class: "flex items-center w-full rounded-full gap-2",
                                          onClick: handleExportToExcel
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_unref(FontAwesomeIcon), {
                                              icon: "fa-solid fa-file-excel",
                                              class: "h-4 text-green"
                                            }),
                                            _createElementVNode("span", null, _toDisplayString(_unref(t)("buttons.export_plain")) + " Excel", 1)
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 2
                                    }, 1032, ["class"])
                                  ]),
                                  _: 1
                                })
                              ])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]))
              : _createCommentVNode("", true)
          ]),
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createTextVNode(_toDisplayString(_ctx.$t("analytics.processes.process_data_table")) + " ", 1),
              (processTable.value?.activeFilters)
                ? (_openBlock(), _createElementBlock("span", {
                    key: 0,
                    class: "underline hover:text-yellow font-normal text-sm text-gray-600 cursor-pointer whitespace-nowrap ml-2",
                    onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (processTable.value?.resetAllFilters && processTable.value?.resetAllFilters(...args)))
                  }, _toDisplayString(_unref(t)("analytics.processes.clear_filters")), 1))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        }),
        (noData.value)
          ? (_openBlock(), _createBlock(NoDataYet, {
              key: 0,
              desc: "analytics.processes.no_data"
            }))
          : _createCommentVNode("", true),
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(LoadingSection, { loading: loading.value }, null, 8, ["loading"])
            ]))
          : (_openBlock(), _createElementBlock("div", {
              key: 2,
              class: "overflow-hidden flex flex-1 relative ml-6 border-box",
              ref_key: "processTableRef",
              ref: processTableRef
            }, [
              _createElementVNode("div", {
                class: "flex-1 flex overflow-hidden flex-col",
                onContextmenu: collectContextMenuStatistics
              }, [
                _createVNode(Table, {
                  columns: columns.value,
                  data: data.value,
                  "onUpdate:data": handleDataUpdate,
                  onDateFilter: handleFilterDate,
                  onRowDbClick: (data) => handleRowClick(data, true),
                  onRowClick: handleRowClick,
                  exportToExcel: handleExportToExcel,
                  sortedBy: sortedBy.value,
                  summary: summary.value,
                  resourceElements: resourceElements.value,
                  width: contentWidth.value - sideBarWidth.value,
                  ref_key: "processTable",
                  ref: processTable
                }, null, 8, ["columns", "data", "onRowDbClick", "sortedBy", "summary", "resourceElements", "width"])
              ], 32),
              _createElementVNode("div", {
                class: "max-w-[50%] min-w-[450px] px-4 relative overflow-y-scroll",
                ref_key: "processSideBarRef",
                ref: processSideBarRef,
                style: _normalizeStyle({
            width: sideBarWidth.value > 0 ? sideBarWidth.value + 'px' : 450 + 'px',
          })
              }, [
                _createVNode(TableResizeSlider, {
                  width: sliderWidth,
                  containerRef: getContainerRef,
                  currentX: 0,
                  onMoved: handleFullSliderMoved,
                  onStarted: _cache[1] || (_cache[1] = ($event: any) => (draggingSliderName.value = 'full')),
                  onEnded: _cache[2] || (_cache[2] = ($event: any) => (draggingSliderName.value = null))
                }),
                _createVNode(ProcessSideBarContent, {
                  processes: selectedProcesses.value,
                  open: true
                }, null, 8, ["processes"])
              ], 4)
            ], 512))
      ])
    ]),
    _: 1
  }, 8, ["activeTag"]))
}
}

})